import { z } from 'zod';

// 様式（帳票）と記録の種類の登録簿。
// 本文（document.body / record.body）の形だけを持つ。ID・法人・事業所・版・状態・確定者は DB の列。

export type AppCode = 'caremanager';

export interface FormDefinition<S extends z.ZodType = z.ZodType> {
  app: AppCode;
  docType: string;
  name: string; // 日本語の様式名
  version: number; // 本文の形の版（document.schema_version）
  schema: S;
  confirmers?: string[]; // 確定できる職種
  description?: string;
}

export interface RecordKindDefinition<S extends z.ZodType = z.ZodType> {
  app: AppCode;
  kind: string;
  name: string;
  version: number; // record.schema_version
  schema: S;
  description?: string;
}

export function defineForm<S extends z.ZodType>(def: FormDefinition<S>): FormDefinition<S> {
  return def;
}

export function defineRecordKind<S extends z.ZodType>(def: RecordKindDefinition<S>): RecordKindDefinition<S> {
  return def;
}

// docType → 様式。index.ts の registerForms で埋める
export const FORMS = new Map<string, FormDefinition>();
export const RECORD_KINDS = new Map<string, RecordKindDefinition>();

export function registerForms(defs: readonly FormDefinition[]): void {
  for (const d of defs) {
    if (FORMS.has(d.docType)) throw new Error(`docType が重複しています: ${d.docType}`);
    FORMS.set(d.docType, d);
  }
}

export function registerRecordKinds(defs: readonly RecordKindDefinition[]): void {
  for (const d of defs) {
    if (RECORD_KINDS.has(d.kind)) throw new Error(`記録の種類が重複しています: ${d.kind}`);
    RECORD_KINDS.set(d.kind, d);
  }
}

export function getForm(docType: string): FormDefinition {
  const f = FORMS.get(docType);
  if (!f) throw new Error(`未登録の様式です: ${docType}`);
  return f;
}

export function getRecordKind(kind: string): RecordKindDefinition {
  const r = RECORD_KINDS.get(kind);
  if (!r) throw new Error(`未登録の記録の種類です: ${kind}`);
  return r;
}

// 本文を検査して整形済みの値を返す。形が違えば ZodError を投げる。
// 版が登録と違うときは移行が要るので、通さずに Error を投げる。
export function validateBody(docType: string, version: number, body: unknown): unknown {
  const f = getForm(docType);
  if (version !== f.version) throw new Error(`${docType} の版 ${version} は未対応です（現行 ${f.version}）`);
  return f.schema.parse(body);
}

export function validateRecordBody(kind: string, version: number, body: unknown): unknown {
  const r = getRecordKind(kind);
  if (version !== r.version) throw new Error(`${kind} の版 ${version} は未対応です（現行 ${r.version}）`);
  return r.schema.parse(body);
}

// ---- 画面組み立て用のメタ情報 ----

export type FieldKind = 'text' | 'longtext' | 'number' | 'date' | 'enum' | 'boolean' | 'array' | 'object';

export interface FieldOption {
  value: string;
  label: string;
}

export interface FieldMeta {
  key: string;
  label: string;
  kind: FieldKind;
  required: boolean;
  options?: FieldOption[];
  item?: FieldMeta; // array の要素
  children?: FieldMeta[]; // object の子
}

// 様式で使うヒント（.meta() に入れる）
export interface FieldHints {
  longtext?: boolean;
  kind?: 'date';
  optionLabels?: Record<string, string>;
  // 表示順。'01' と '10' のようなキーは JS のオブジェクトで並びが崩れるので、これで並べ直す
  order?: number;
}

type AnyDef = { type: string; [k: string]: unknown };
const defOf = (s: z.ZodType): AnyDef => (s as unknown as { _zod: { def: AnyDef } })._zod.def;

const WRAPPERS = new Set(['optional', 'nullable', 'default', 'prefault', 'nonoptional', 'readonly', 'catch']);

// optional/default などの包みを剥がし、途中のメタ情報（describe/meta）を外側優先で合わせる
function unwrap(s: z.ZodType): { inner: z.ZodType; required: boolean; meta: Record<string, unknown> } {
  let cur = s;
  let required = true;
  const metas: Record<string, unknown>[] = [];
  for (;;) {
    const m = z.globalRegistry.get(cur);
    if (m) metas.push(m as Record<string, unknown>);
    const d = defOf(cur);
    if (d.type === 'optional' || d.type === 'default' || d.type === 'prefault' || d.type === 'catch') required = false;
    if (d.type === 'pipe') {
      cur = d.in as z.ZodType;
      continue;
    }
    if (WRAPPERS.has(d.type)) {
      cur = d.innerType as z.ZodType;
      continue;
    }
    break;
  }
  const meta: Record<string, unknown> = {};
  for (const m of metas.reverse()) Object.assign(meta, m);
  return { inner: cur, required, meta };
}

function describeField(key: string, s: z.ZodType): FieldMeta {
  const { inner, required, meta } = unwrap(s);
  const hints = meta as FieldHints & { description?: string };
  const label = hints.description ?? key;
  const d = defOf(inner);
  const base = { key, label, required };
  switch (d.type) {
    case 'string':
      return { ...base, kind: hints.kind === 'date' ? 'date' : hints.longtext ? 'longtext' : 'text' };
    case 'number':
    case 'int':
    case 'bigint':
      return { ...base, kind: 'number' };
    case 'boolean':
      return { ...base, kind: 'boolean' };
    case 'enum': {
      const values = Object.values(d.entries as Record<string, string>);
      return { ...base, kind: 'enum', options: values.map((v) => ({ value: v, label: hints.optionLabels?.[v] ?? v })) };
    }
    case 'literal': {
      const values = (d.values as unknown[]).map(String);
      return { ...base, kind: 'enum', options: values.map((v) => ({ value: v, label: hints.optionLabels?.[v] ?? v })) };
    }
    case 'array':
      return { ...base, kind: 'array', item: describeField('item', d.element as z.ZodType) };
    case 'object':
      return { ...base, kind: 'object', children: objectFields(inner) };
    default:
      // record など: 中身は自由な対応表として扱う
      return { ...base, kind: 'object' };
  }
}

function objectFields(s: z.ZodType): FieldMeta[] {
  const shape = defOf(s).shape as Record<string, z.ZodType>;
  const order = (v: z.ZodType) => (unwrap(v).meta as FieldHints).order ?? Number.POSITIVE_INFINITY;
  return Object.entries(shape)
    .map(([k, v], i) => ({ i, o: order(v), f: describeField(k, v) }))
    .sort((a, b) => a.o - b.o || a.i - b.i)
    .map((x) => x.f);
}

// 様式の本文の最上位の項目一覧（object/array の中身は children/item に入る）
export function fieldMeta(docType: string): FieldMeta[] {
  return schemaFieldMeta(getForm(docType).schema);
}

export function schemaFieldMeta(schema: z.ZodType): FieldMeta[] {
  const { inner } = unwrap(schema);
  if (defOf(inner).type !== 'object') throw new Error('本文のスキーマは z.object である必要があります');
  return objectFields(inner);
}
